import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiCircle,
  FiLoader,
  FiEye,
  FiCheckCircle,
  FiAlertOctagon,
  FiAlertTriangle,
  FiX,
  FiInbox,
} from "react-icons/fi";
import TaskCard from "./TaskCard";
import "./KanbanBoard.css";

const KanbanBoard = ({
  board,
  onTaskClick,
  loading,
  searchQuery = "",
  filterPriority = "all",
}) => {
  const [selectedColumn, setSelectedColumn] = useState(null);

  const columns = [
    {
      id: "todo",
      title: "To Do",
      icon: FiCircle,
      color: "#6366f1",
      bgColor: "#eef2ff",
      borderColor: "#c7d2fe",
    },
    {
      id: "in-progress",
      title: "In Progress",
      icon: FiLoader,
      color: "#f59e0b",
      bgColor: "#fffbeb",
      borderColor: "#fde68a",
    },
    {
      id: "review",
      title: "Review",
      icon: FiEye,
      color: "#8b5cf6",
      bgColor: "#f5f3ff",
      borderColor: "#ddd6fe",
    },
    {
      id: "completed",
      title: "Completed",
      icon: FiCheckCircle,
      color: "#10b981",
      bgColor: "#ecfdf5",
      borderColor: "#a7f3d0",
    },
    {
      id: "blocked",
      title: "Blocked",
      icon: FiAlertOctagon,
      color: "#ef4444",
      bgColor: "#fef2f2",
      borderColor: "#fecaca",
    },
    {
      id: "overdue",
      title: "Overdue",
      icon: FiAlertTriangle,
      color: "#dc2626",
      bgColor: "#fef2f2",
      borderColor: "#fca5a5",
    },
  ];

  // Filter tasks based on search query
  const filteredBoard = useMemo(() => {
    if (!board) return {};
    if (!searchQuery.trim()) return board;

    const query = searchQuery.toLowerCase();
    const filtered = {};

    Object.keys(board).forEach((columnId) => {
      filtered[columnId] = board[columnId].filter(
        (task) =>
          task.title?.toLowerCase().includes(query) ||
          task.description?.toLowerCase().includes(query) ||
          task.assignedTo?.name?.toLowerCase().includes(query)
      );
    });

    return filtered;
  }, [board, searchQuery]);

  if (loading) {
    return (
      <div className="rkb-loading">
        <div className="rkb-loading-spinner">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <FiLoader size={32} />
          </motion.div>
        </div>
        <p className="rkb-loading-text">Loading tasks...</p>
      </div>
    );
  }

  if (!board || Object.keys(board).length === 0) {
    return (
      <div className="rkb-empty">
        <div className="rkb-empty-icon">
          <FiInbox size={48} />
        </div>
        <h3 className="rkb-empty-title">No tasks yet</h3>
        <p className="rkb-empty-description">
          Create your first task to start organizing your work
        </p>
      </div>
    );
  }

  const handleColumnClick = (column) => {
    setSelectedColumn(column);
  };

  const handleCloseModal = () => {
    setSelectedColumn(null);
  };

  // Get total count for display
  const getTotalTaskCount = () => {
    return Object.values(filteredBoard).reduce(
      (acc, tasks) => acc + tasks.length,
      0
    );
  };

  return (
    <div className="rkb-container">
      {/* Status Cards Overview */}
      <div className="rkb-overview">
        {columns.map((column, index) => {
          const Icon = column.icon;
          const tasks = filteredBoard[column.id] || [];
          const taskCount = tasks.length;

          return (
            <motion.button
              key={column.id}
              className="rkb-status-card"
              onClick={() => handleColumnClick(column)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              whileHover={{ y: -4, boxShadow: "0 12px 24px rgba(0,0,0,0.1)" }}
              whileTap={{ scale: 0.98 }}
              style={{
                "--card-color": column.color,
                "--card-bg": column.bgColor,
                "--card-border": column.borderColor,
              }}
            >
              <div className="rkb-status-icon">
                <Icon size={20} />
              </div>
              <div className="rkb-status-info">
                <span className="rkb-status-count">{taskCount}</span>
                <span className="rkb-status-label">{column.title}</span>
              </div>
              <div className="rkb-status-indicator" />
            </motion.button>
          );
        })}
      </div>

      {/* Task List Modal/Drawer */}
      <AnimatePresence>
        {selectedColumn && (
          <motion.div
            className="rkb-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
          >
            <motion.div
              className="rkb-modal"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div
                className="rkb-modal-header"
                style={{ "--header-color": selectedColumn.color }}
              >
                <div className="rkb-modal-title-group">
                  <div className="rkb-modal-icon">
                    {React.createElement(selectedColumn.icon, { size: 20 })}
                  </div>
                  <div>
                    <h2 className="rkb-modal-title">{selectedColumn.title}</h2>
                    <p className="rkb-modal-subtitle">
                      {(filteredBoard[selectedColumn.id] || []).length}{" "}
                      {(filteredBoard[selectedColumn.id] || []).length === 1
                        ? "task"
                        : "tasks"}
                    </p>
                  </div>
                </div>
                <button className="rkb-modal-close" onClick={handleCloseModal}>
                  <FiX size={20} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="rkb-modal-body">
                {(filteredBoard[selectedColumn.id] || []).length === 0 ? (
                  <div className="rkb-modal-empty">
                    <FiInbox size={40} />
                    <p>No tasks in this column</p>
                  </div>
                ) : (
                  <div className="rkb-task-list">
                    {(filteredBoard[selectedColumn.id] || []).map(
                      (task, idx) => (
                        <motion.div
                          key={task._id}
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.04 }}
                        >
                          <TaskCard
                            task={task}
                            onClick={() => {
                              onTaskClick(task);
                              handleCloseModal();
                            }}
                            accentColor={selectedColumn.color}
                          />
                        </motion.div>
                      )
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default KanbanBoard;
